import { useDispatch, useSelector } from "react-redux";
import SubHeader from "../../../components/SubHeader";
import { RootState } from "../../../store/store";
import { CreatorMode, EditorMode } from "../../../types/editorModeEnum";
import { useEffect, useState } from "react";
import { IScenario } from "../../../types/IScenario";
import SimpleScenario from "../../../data/SimpleScenario";
import Divider from "../../../components/Divider";
import ScenarioUtils from "../../../utils/scenarioUtils";
import Footer from "../../../components/Footer";
import { setCreatorMode, setEditorMode, setScenarios, setSelectedScenario } from "../../../store/editorSlice";
import RoleSetup from "./RoleSetup";
import LocalStorageService from "../../../services/localStorageService";
import { GuidGenerator } from "../../../utils/guidGenerator";
import { showErrorToast } from "../../../store/messageSlice";
import RoleString from "../../../components/RoleString";

function Creator() {
    const dispatch = useDispatch();
    const { editorMode, creatorMode, scenarios, selectedScenario } = useSelector((state: RootState) => state.editor);

    const [scenario, setScenario] = useState<IScenario>({
        ...(SimpleScenario as IScenario),
        name: `${(SimpleScenario as IScenario).name} ${scenarios.length + 1}`
    });

    useEffect(() => {
        if(!selectedScenario) {
            const guid = GuidGenerator.generateGUID();
            setSelectedScenario(guid);
            setScenario({
                ...(SimpleScenario as IScenario),
                name: `${(SimpleScenario as IScenario).name} ${scenarios.length + 1}`,
                guid
            });
        } else {
            const scenario = scenarios.find(s => s.guid === selectedScenario);
            if(scenario){
                setScenario(scenario);
            } else {
                dispatch(showErrorToast("Could not load scenario"));
            }
        }
    }, [])

    const onNameChange = (e: any) => setScenario({ ...scenario, name: e.target.value });
    const onDayDurationChange = (e: any) => setScenario({ ...scenario, day_duration: +e.target.value });
    const onNightDurationChange = (e: any) => setScenario({ ...scenario, night_duration: +e.target.value });

    const getInputRow = (label: string, value: string | number, callback: (e: any) => void) => {
        return <div className="mafia-editor-row">
            <div className="label">{label}:</div>
            <input className="input"
                type={typeof value === "string" ? 'text' : 'number'}
                value={value}
                onChange={callback}
                name={label}></input>
        </div>
    }

    const getSetupRolesSection = () => {
        const roleSetupString = <div className="bigger mark">{scenario.raw_scenario.length}</div>
        return <div>
            <div className="mafia-editor-row centered">
                <div>Role Setup:</div>
                <div className="content">{roleSetupString}</div>
            </div>
            <div className="mafia-editor-row centered">
                <div className="content"><RoleString raw_scenario={scenario.raw_scenario} /></div>
            </div>
            <div className="mafia-editor-row centered">
                <button className="button" onClick={onSetupRolesClick}>
                    Role Setup
                </button>
            </div>
        </div>
    }

    const getCustomizeRolesSection = () => {
        return <div>
            <div className="mafia-editor-row centered">Advanced:</div>
            <div className="mafia-editor-row centered">
                <button className="button disabled" onClick={oncustomizeRolesClick}>
                    Role Customization
                </button>
            </div>
        </div>
    }

    const onSetupRolesClick = () => dispatch(setCreatorMode(CreatorMode.SetupRoles));
    const oncustomizeRolesClick = () => dispatch(setCreatorMode(CreatorMode.CustomizeRoles));
    const onCancel = () => {
        switch (creatorMode) {
            case CreatorMode.SetupRoles:
            case CreatorMode.CustomizeRoles:
                dispatch(setCreatorMode(CreatorMode.Main));
                break;
            case CreatorMode.Main:
            default:
                dispatch(setEditorMode(EditorMode.List))
        }
    };
    const onScenarioSave = () => {
        const existingScenario = scenarios.find(s => s.guid === scenario.guid);
        if(existingScenario) {
            const clone = [
                ...scenarios,
            ]
            for(var i=0; i<clone.length; i++) {
                if(clone[i].guid === existingScenario.guid){
                    clone[i] = scenario;
                    break;
                }
            }
            setScenarios(clone);
            LocalStorageService.SaveScenarios(clone);
        } else {
            const clone = [
                ...scenarios,
                scenario,
            ]
            dispatch(setScenarios(clone));
            LocalStorageService.SaveScenarios(clone);
        }        
        dispatch(setSelectedScenario(''));
        dispatch(setEditorMode(EditorMode.List));
     }

    const onRoleSetupSave = (edited_raw_scenario: string[]) => {
        setScenario({
            ...scenario,
            raw_scenario: edited_raw_scenario,
        })
    }

    const getContent = () => {
        switch (creatorMode) {
            case CreatorMode.SetupRoles:
            case CreatorMode.SetupRoleRow:
            case CreatorMode.SetupSingleRoleRow:
                return <RoleSetup raw_scenario={scenario.raw_scenario} onSave={onRoleSetupSave}/>
            case CreatorMode.CustomizeRoles:
                //TODO: Implement this view
                return <RoleSetup raw_scenario={scenario.raw_scenario} onSave={onRoleSetupSave}/>
            case CreatorMode.Main:
            default:
                return <div className="mafia-container-wrapper">
                    <div className="mafia-content">
                        <SubHeader>Scenario {editorMode === EditorMode.Edit ? 'Editor' : 'Creator'}</SubHeader>
                        <div className="mafia-editor-container">
                            {getInputRow('Name', scenario.name, onNameChange)}
                            {getInputRow('Day Duration', scenario.day_duration, onDayDurationChange)}
                            {getInputRow('Night Duration', scenario.night_duration, onNightDurationChange)}
                            <Divider />
                            {getSetupRolesSection()}
                            <Divider />
                            {getCustomizeRolesSection()}
                        </div>
                    </div>
                    <Footer>
                        <button onClick={onCancel}>Cancel</button>
                        <button onClick={onScenarioSave}>Save</button>
                    </Footer>
                </div>
        }
    }

    return getContent();
}

export default Creator;